import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const requestSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  optOut: z.boolean(),
  externalId: z.string().trim().max(128).optional(),
});

const confirmSchema = z.object({
  token: z.string().trim().min(16).max(128),
});

const TOKEN_TTL_MS = 24 * 60 * 60 * 1000;
const MAX_PENDING_PER_HOUR = 3;

/**
 * Requests a privacy opt-out/opt-in change for an email address.
 *
 * This does NOT touch Shopify or Meta. It mints a one-time confirmation
 * token, stores only its hash, and emails the address a confirmation link.
 * The response is always the same generic acknowledgement so the endpoint
 * cannot be used to probe whether an address is a customer.
 */
export const submitPrivacyOptOut = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => requestSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      const { randomBytes, createHash } = await import("node:crypto");
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { sendPrivacyConfirmationEmail } = await import("./privacyEmail.server");

      // Throttle: at most a few unconsumed requests per address per hour.
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count } = await supabaseAdmin
        .from("privacy_optout_tokens")
        .select("id", { count: "exact", head: true })
        .eq("email", data.email)
        .is("consumed_at", null)
        .gte("created_at", oneHourAgo);

      if ((count ?? 0) >= MAX_PENDING_PER_HOUR) {
        return { ok: true };
      }

      const token = randomBytes(32).toString("hex");
      const tokenHash = createHash("sha256").update(token).digest("hex");

      // Invalidate any still-pending links for this address so only the
      // newest confirmation link is usable. Consumed rows are left intact.
      const nowIso = new Date().toISOString();
      await supabaseAdmin
        .from("privacy_optout_tokens")
        .update({ expires_at: nowIso })
        .eq("email", data.email)
        .is("consumed_at", null)
        .gt("expires_at", nowIso);


      const { error } = await supabaseAdmin.from("privacy_optout_tokens").insert({
        token_hash: tokenHash,
        email: data.email,
        opt_out: data.optOut,
        external_id: data.externalId ?? null,
        expires_at: new Date(Date.now() + TOKEN_TTL_MS).toISOString(),
      });
      if (error) throw error;

      await sendPrivacyConfirmationEmail(data.email, token, data.optOut);
    } catch (e) {
      // Swallow: the response must be identical regardless of outcome.
      console.error("privacy opt-out request failed", e);
    }

    return { ok: true };
  });

/**
 * Consumes a confirmation token and applies the change in Shopify + Meta.
 * Returns only whether the link was valid — never per-provider detail.
 */
export const confirmPrivacyOptOut = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => confirmSchema.parse(input))
  .handler(async ({ data }) => {
    const { createHash } = await import("node:crypto");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const tokenHash = createHash("sha256").update(data.token).digest("hex");

    // Atomically claim the token: only an unconsumed, unexpired row matches.
    const { data: claimed, error: claimError } = await supabaseAdmin
      .from("privacy_optout_tokens")
      .update({ consumed_at: new Date().toISOString() })
      .eq("token_hash", tokenHash)
      .is("consumed_at", null)
      .gt("expires_at", new Date().toISOString())
      .select("email, opt_out, external_id")
      .maybeSingle();

    if (claimError || !claimed) {
      return { ok: false as const };
    }

    const { syncShopifyOptOut, syncMetaOptOut } = await import("./privacy.server");
    const [shopify, meta] = await Promise.all([
      syncShopifyOptOut(claimed.email, claimed.opt_out),
      syncMetaOptOut(claimed.email, claimed.opt_out, claimed.external_id ?? undefined),
    ]);

    // Best-effort audit log; never block the response on this.
    try {
      await supabaseAdmin.from("privacy_optout_syncs").insert([
        {
          email: claimed.email,
          provider: shopify.provider,
          status: shopify.status,
          error: shopify.error ?? null,
          opt_out: claimed.opt_out,
        },
        {
          email: claimed.email,
          provider: meta.provider,
          status: meta.status,
          error: meta.error ?? null,
          opt_out: claimed.opt_out,
        },
      ]);
    } catch {
      // ignore
    }

    return { ok: true as const, optOut: claimed.opt_out };
  });
